import clientEnv from '@savvy/env/client';

const DEPLOYMENT_ID_HEADER = 'x-deployment-id';

type DeploymentOutdatedHandler = () => void;

let onDeploymentOutdated: DeploymentOutdatedHandler | null = null;

/**
 * Register the handler invoked when a skew-pinned request 404s — the client's
 * pinned deployment aged out past Vercel's Max Age. The version-skew refresh
 * controller registers this (mirrors `setOnNetworkAuthError`); pass null to clear.
 */
export function setOnDeploymentOutdated(handler: DeploymentOutdatedHandler | null): void {
  onDeploymentOutdated = handler;
}

// Vercel sets VERCEL_SKEW_PROTECTION_ENABLED to '1' when the feature is on; both
// vars are build-inlined as NEXT_PUBLIC_ names (next.config.mjs) so they read in
// the browser. Returns the id to pin with, or null when skew protection is off.
function deploymentIdForPinning(): string | null {
  if (clientEnv.NEXT_PUBLIC_VERCEL_SKEW_PROTECTION_ENABLED !== '1') return null;
  const id = clientEnv.NEXT_PUBLIC_VERCEL_DEPLOYMENT_ID;
  return id !== undefined && id !== '' ? id : null;
}

/**
 * Drop-in `fetch` for calls to our own `/api/*` route handlers. Vercel Skew
 * Protection does not auto-pin custom `fetch()` calls, so when it's enabled this
 * attaches `x-deployment-id` to route the request to the client's own deployment.
 * Returns the raw `Response` so streaming (`response.body.getReader()`) is
 * unaffected. A 404 on a pinned request means that deployment aged out — it's
 * routed to the registered outdated handler (see `setOnDeploymentOutdated`).
 */
export async function apiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const deploymentId = deploymentIdForPinning();

  // Skew protection off → behave exactly like `fetch`, leaving a `Request`
  // input's own headers untouched.
  if (deploymentId === null) {
    return fetch(input, init);
  }

  // Seed from init.headers, falling back to the Request's own headers when init
  // doesn't override them, so the pin augments rather than drops caller headers.
  const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
  headers.set(DEPLOYMENT_ID_HEADER, deploymentId);

  const response = await fetch(input, { ...init, headers });

  // A pinned 404 means the pinned deployment aged out — but this can't be
  // distinguished from a genuine route-handler 404 (e.g. a deleted resource), so
  // the Task 6 refresh controller must corroborate/debounce before reloading.
  // Isolate the handler so a throwing refresh policy can never eat the caller's
  // Response (the drop-in contract).
  if (response.status === 404) {
    try {
      onDeploymentOutdated?.();
    } catch {
      // A refresh-policy failure must not break the fetch contract.
    }
  }

  return response;
}
